import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"

import {
  previewReceiptConfiguration,
  removeReceiptLogo,
  updateReceiptConfiguration,
  uploadReceiptLogo,
} from "src/api/PaperReceipts"

import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"

import ReceiptPreview from "./ReceiptPreview"

const ReceiptConfiguration = ({ configuration }) => {
  const showToast = useToast()
  const [logoImageId, setLogoImageId] = useState(
    configuration?.logoImageId || null
  )
  const [logoUrl, setLogoUrl] = useState(configuration?.logoUrl || null)
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...configuration,
      logoImageId: configuration?.logoImageId || null,
    },
  })

  // Watch specific fields that affect the preview
  const showMarinaName = watch("showMarinaName")
  const showMarinaAddress = watch("showMarinaAddress")
  const showMarinaPhone = watch("showMarinaPhone")
  const showMarinaEmail = watch("showMarinaEmail")
  const promotionalMessage = watch("promotionalMessage")
  const showPromotionalMessage = watch("showPromotionalMessage")
  const qrCodeData = watch("qrCodeData")
  const showQrCode = watch("showQrCode")
  const showLogo = watch("showLogo")

  const [debouncedPromotionalMessage, setDebouncedPromotionalMessage] =
    useState(promotionalMessage || "")
  const [debouncedQrCodeData, setDebouncedQrCodeData] = useState(
    qrCodeData || ""
  )
  const [debouncedUpdatePromotionalMessage] = useDebounce(
    setDebouncedPromotionalMessage,
    500
  )
  const [debouncedUpdateQrCodeData] = useDebounce(setDebouncedQrCodeData, 500)

  useEffect(() => {
    debouncedUpdatePromotionalMessage(promotionalMessage || "")
  }, [promotionalMessage, debouncedUpdatePromotionalMessage])

  useEffect(() => {
    debouncedUpdateQrCodeData(qrCodeData || "")
  }, [qrCodeData, debouncedUpdateQrCodeData])

  // Create a stable query key from form values (using debounced values for text inputs)
  const queryKey = useMemo(
    () => [
      "receiptPreview",
      {
        showMarinaName,
        showMarinaAddress,
        showMarinaPhone,
        showMarinaEmail,
        promotionalMessage: debouncedPromotionalMessage,
        showPromotionalMessage,
        qrCodeData: debouncedQrCodeData,
        showQrCode,
        showLogo,
        logoImageId,
      },
    ],
    [
      showMarinaName,
      showMarinaAddress,
      showMarinaPhone,
      showMarinaEmail,
      debouncedPromotionalMessage,
      showPromotionalMessage,
      debouncedQrCodeData,
      showQrCode,
      showLogo,
      logoImageId,
    ]
  )

  const { data: previewData, isLoading } = useQuery(
    queryKey,
    () => previewReceiptConfiguration(queryKey[1]),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  )

  const updateConfigurationMutation = useMutation(updateReceiptConfiguration, {
    onSuccess: () => {
      showToast("Configuration saved successfully!", { type: "success" })
    },
    onError: () => {
      showToast("Failed to save configuration. Please try again.", {
        type: "error",
      })
    },
  })

  const uploadLogoMutation = useMutation(uploadReceiptLogo, {
    onSuccess: (result) => {
      setLogoImageId(result.logoImageId)
      setLogoUrl(result.logoUrl)
      setValue("logoImageId", result.logoImageId, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    onError: () => {
      showToast("Upload failed. Please try again.", { type: "error" })
    },
    onSettled: () => {
      setIsFileSelectorOpen(false)
    },
  })

  const onSubmit = (data) => {
    updateConfigurationMutation.mutate(data)
  }

  const handleFileSelect = (file) => {
    // Validate file type
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      showToast("Only PNG, JPEG, and WebP files are supported", {
        type: "error",
      })
      setIsFileSelectorOpen(false)
      return
    }

    // Validate file size (10MB)
    if (file.size > 10000000) {
      showToast("File size must be less than 10MB", { type: "error" })
      setIsFileSelectorOpen(false)
      return
    }

    uploadLogoMutation.mutate(file)
  }

  const removeLogoMutation = useMutation(removeReceiptLogo, {
    onSuccess: () => {
      setLogoImageId(null)
      setLogoUrl(null)
      setValue("logoImageId", null, { shouldValidate: true, shouldDirty: true })
    },
    onError: () => {
      showToast("Failed to remove logo. Please try again.", { type: "error" })
    },
  })

  const handleRemoveLogo = () => {
    removeLogoMutation.mutate()
  }

  return (
    <div className="flex h-full gap-8">
      <div className="w-1/2 space-y-6">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Logo</h3>

            <div>
              <Form.Label htmlFor="logoUpload">Logo Image</Form.Label>
              {logoUrl ? (
                <div className="space-y-3">
                  {/* Logo Preview */}
                  <div className="bg-gray-50 flex justify-center rounded border border-gray-200 p-4">
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      className="max-h-32 max-w-full object-contain"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      onClick={() => setIsFileSelectorOpen(true)}
                      disabled={uploadLogoMutation.isLoading}
                    >
                      Replace
                    </Button>
                    <Button
                      variant="tertiary"
                      onClick={handleRemoveLogo}
                      disabled={removeLogoMutation.isLoading}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="bg-gray-50 w-full cursor-pointer rounded border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 hover:bg-gray-100"
                  onClick={() =>
                    !uploadLogoMutation.isLoading && setIsFileSelectorOpen(true)
                  }
                >
                  <div className="text-gray-600">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="mt-2 text-sm font-medium">
                      {uploadLogoMutation.isLoading
                        ? "Uploading..."
                        : "Upload Logo"}
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPEG, or WebP up to 10MB
                    </p>
                  </div>
                </div>
              )}

              <Form.FileSelector
                isOpen={isFileSelectorOpen}
                onSelect={handleFileSelect}
                onClose={() => setIsFileSelectorOpen(false)}
                dragAndDropEnabled={true}
                isLoading={uploadLogoMutation.isLoading}
                heading="Upload Receipt Logo"
                description="Upload a PNG, JPEG, or WebP logo for your receipts (max 10MB)"
                acceptedFiles=".png,.jpg,.jpeg,.webp"
                maxFileSize={10000000}
              />
            </div>

            <Form.Checkbox
              label="Show Logo"
              id="showLogo"
              {...register("showLogo")}
              hasErrors={!!errors?.showLogo}
            />
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Marina Information
            </h3>

            <div className="space-y-3">
              <Form.Checkbox
                id="showMarinaName"
                label="Show Marina Name"
                {...register("showMarinaName")}
                hasErrors={!!errors?.showMarinaName}
              />
              <Form.Checkbox
                id="showMarinaAddress"
                label="Show Marina Address"
                {...register("showMarinaAddress")}
                hasErrors={!!errors?.showMarinaAddress}
              />
              <Form.Checkbox
                id="showMarinaPhone"
                label="Show Marina Phone"
                {...register("showMarinaPhone")}
                hasErrors={!!errors?.showMarinaPhone}
              />
              <Form.Checkbox
                id="showMarinaEmail"
                label="Show Marina Email"
                {...register("showMarinaEmail")}
                hasErrors={!!errors?.showMarinaEmail}
              />
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">QR Code</h3>

            <Form.Checkbox
              id="showQrCode"
              label="Show QR Code"
              {...register("showQrCode")}
              hasErrors={!!errors?.showQrCode}
            />

            {showQrCode && (
              <div>
                <Form.Label htmlFor="qrCodeData">QR Code Data</Form.Label>
                <Form.TextField
                  id="qrCodeData"
                  {...register("qrCodeData")}
                  hasErrors={!!errors?.qrCodeData}
                  placeholder="Enter URL or text for QR code"
                />
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Promotional Message
            </h3>

            <Form.Checkbox
              id="showPromotionalMessage"
              label="Show Promotional Message"
              {...register("showPromotionalMessage")}
              hasErrors={!!errors?.showPromotionalMessage}
            />

            {showPromotionalMessage && (
              <div>
                <Form.Label htmlFor="promotionalMessage">
                  Promotional Message
                </Form.Label>
                <Form.Textarea
                  id="promotionalMessage"
                  {...register("promotionalMessage")}
                  placeholder="Enter a promotional message for your receipts"
                  rows={3}
                />
              </div>
            )}
          </div>
          <div className="pt-6">
            <Button
              type="submit"
              variant="primary"
              disabled={updateConfigurationMutation.isLoading}
              isLoading={updateConfigurationMutation.isLoading}
            >
              Save Configuration
            </Button>
          </div>
        </Form>
      </div>
      <div className="w-1/2">
        <div className="sticky top-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Receipt Preview
          </h3>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            {isLoading ? (
              <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
                  <p className="text-gray-500">Generating preview...</p>
                </div>
              </div>
            ) : previewData?.base64 ? (
              <ReceiptPreview
                base64={previewData.base64}
                lineHeight={20}
                font="14px monospace"
              />
            ) : (
              <div className="flex h-96 items-center justify-center">
                <p className="text-gray-500">Preview will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

ReceiptConfiguration.propTypes = {
  configuration: PropTypes.object.isRequired,
}

export default ReceiptConfiguration
